import React, { useState, useEffect, useRef } from 'react';
import { Download, FileText, Pencil, Trash2, Upload } from 'lucide-react';
import { staffClient } from 'clients/staff';
import { getS3UploadUrl, uploadS3File } from 'utils/s3';
import { FileUtils } from 'components/Utils/file';
import Tabs from 'ui/Tabs';
import Section from 'ui/Section';
import TableContainer from 'ui/TableContainer';
import Table, { Column } from 'ui/Table';
import Button from 'ui/Button';
import Input from 'ui/Input';
import Modal from 'ui/Modal';
import Loading from 'ui/Loading';
import { cn } from 'lib/utils';

interface Category {
    id: number;
    name: string;
    order: number;
}

interface Board {
    id: number;
    name: string;
    board_type: 'general' | 'document';
    document_category?: string;
    categories?: Category[];
}

interface Document {
    id: number;
    title: string;
    created_at: string;
    writer?: {
        name: string;
        user_id: string;
    };
    files?: Array<{
        id: number;
        name: string;
        size: number;
        url: string;
    }>;
}

interface DocumentLibraryProps {
    siteId: string | undefined;
    onFileUpload?: (boardId: number, categoryId: number, file: File) => void;
    onFileDownload?: (fileId: number) => void;
}

const DocumentLibrary: React.FC<DocumentLibraryProps> = ({ siteId, onFileUpload, onFileDownload }) => {
    const [boards, setBoards] = useState<Board[]>([]);
    const [siteName, setSiteName] = useState('');
    const [selectedBoard, setSelectedBoard] = useState<Board | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [uploading, setUploading] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadTitle, setUploadTitle] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [categoryCounts, setCategoryCounts] = useState<{ [key: number]: number }>({});
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingDocument, setEditingDocument] = useState<Document | null>(null);
    const [editTitle, setEditTitle] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    // 문서 카테고리 한글 매핑
    const categoryNames: { [key: string]: string } = {
        'general_mgmt': '일반관리',
        'design_mgmt': '설계관리',
        'process_mgmt': '공정관리',
        'construction_mgmt': '시공관리',
        'completion_mgmt': '준공관리'
    };

    useEffect(() => {
        if (siteId) {
            fetchSiteBoards();
        }
    }, [siteId]);

    useEffect(() => {
        if (selectedBoard && selectedCategory) {
            fetchDocuments();
        }
    }, [selectedBoard, selectedCategory]);

    useEffect(() => {
        if (selectedBoard && selectedBoard.categories) {
            fetchCategoryCounts();
        }
    }, [selectedBoard]);

    const fetchSiteBoards = async () => {
        try {
            setLoading(true);
            const response = await staffClient.get(`/site/${siteId}`);
            const siteData = response.data;
            setSiteName(siteData?.name || '');

            // 문서 게시판만 필터링
            const documentBoards = siteData.boards?.filter((board: Board) =>
                board.board_type === 'document'
            ) || [];

            setBoards(documentBoards);

            // 첫 번째 게시판 자동 선택
            if (documentBoards.length > 0) {
                setSelectedBoard(documentBoards[0]);
                // 첫 번째 카테고리 자동 선택
                if (documentBoards[0].categories && documentBoards[0].categories.length > 0) {
                    setSelectedCategory(documentBoards[0].categories[0].id);
                }
            }
        } catch (error) {
            console.error('Failed to fetch site boards:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchDocuments = async () => {
        if (!selectedBoard || !selectedCategory) return;

        try {
            const response = await staffClient.get('/article', {
                params: {
                    boardId: selectedBoard.id,
                    categoryId: selectedCategory,
                    limit: 100,
                    offset: 0
                }
            });

            // 응답 데이터 구조에 맞게 처리
            const articles = response.data?.data || [];
            setDocuments(articles);
        } catch (error) {
            console.error('Failed to fetch documents:', error);
            setDocuments([]);
        }
    };

    const fetchCategoryCounts = async () => {
        if (!selectedBoard || !selectedBoard.categories) return;

        const counts: { [key: number]: number } = {};

        try {
            // 각 카테고리별로 문서 개수 조회
            const promises = selectedBoard.categories.map(async (category) => {
                try {
                    const response = await staffClient.get('/article', {
                        params: {
                            boardId: selectedBoard.id,
                            categoryId: category.id,
                            limit: 1,
                            offset: 0
                        }
                    });

                    // meta.total에서 전체 개수 가져오기
                    const total = response.data?.meta?.total || 0;
                    counts[category.id] = total;
                } catch (error) {
                    console.error(`Failed to fetch count for category ${category.id}:`, error);
                    counts[category.id] = 0;
                }
            });

            await Promise.all(promises);
            setCategoryCounts(counts);
        } catch (error) {
            console.error('Failed to fetch category counts:', error);
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setShowUploadModal(true);
            // 파일 입력 초기화
            event.target.value = '';
        }
    };

    const handleFileUpload = async () => {
        if (!selectedFile || !selectedBoard || !selectedCategory || !uploadTitle) {
            alert('제목을 입력해주세요.');
            return;
        }

        setUploading(true);
        try {
            // 1. 파일 크기 체크 (10MB)
            if (!FileUtils.checkFileSize(selectedFile, 10)) {
                setUploading(false);
                return;
            }

            // 2. S3 presigned URL 가져오기
            const uploadUrl = await getS3UploadUrl(selectedFile);
            if (!uploadUrl) {
                throw new Error('Failed to get upload URL');
            }

            // 3. S3에 파일 업로드
            const finalUrl = await uploadS3File(selectedFile, uploadUrl);
            if (!finalUrl) {
                throw new Error('Failed to upload file to S3');
            }

            // 4. 파일 해시 생성 (선택사항)
            const fileHash = await FileUtils.getFileHash(selectedFile);

            // 5. Article 생성 API 호출
            // 파일명이 너무 길면 잘라내기 (확장자 유지)
            let fileName = selectedFile.name;
            const maxNameLength = 100;
            if (fileName.length > maxNameLength) {
                const ext = fileName.split('.').pop();
                const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
                fileName = nameWithoutExt.substring(0, maxNameLength - ext.length - 5) + '...' + '.' + ext;
            }

            const fileInfo = {
                name: fileName,
                url: finalUrl, // S3 CDN URL
                type: selectedFile.type || 'application/octet-stream',
                size: selectedFile.size,
                hash: fileHash ? fileHash.substring(0, 100) : '' // hash도 100자로 제한
            };

            await staffClient.post('/article', {
                title: uploadTitle,
                contents: { type: 'document' }, // 문서 타입 표시
                board_id: selectedBoard.id,
                category_id: selectedCategory,
                files: [fileInfo]
            });

            // 성공 시 모달 닫기 및 초기화
            setShowUploadModal(false);
            setUploadTitle('');
            setSelectedFile(null);

            // 문서 목록 및 카운트 새로고침
            await fetchDocuments();
            await fetchCategoryCounts();

            alert('파일이 업로드되었습니다.');
        } catch (error) {
            console.error('Failed to upload file:', error);
            alert('파일 업로드에 실패했습니다.');
        } finally {
            setUploading(false);
        }
    };

    /**
     * 다운로드 이력 기록 — 보안 로그(누가 어떤 파일을 어디서 받았는지)용.
     * 기록이 실패해도 다운로드 자체는 막지 않는다 (에러는 콘솔만).
     */
    const logDownload = async (file: { name: string; url: string }) => {
        try {
            const numericSiteId = Number(siteId);
            await staffClient.post('/download-log', {
                fileName: file.name,
                fileUrl: file.url,
                siteId: Number.isFinite(numericSiteId) && numericSiteId > 0 ? numericSiteId : undefined,
                siteName: siteName || undefined,
                boardId: selectedBoard?.id,
                boardName: selectedBoard ? getBoardLabel(selectedBoard) : undefined,
                categoryName:
                    selectedBoard?.categories?.find((c) => c.id === selectedCategory)?.name ||
                    undefined,
            });
        } catch (error) {
            console.error('[DocumentLibrary] 다운로드 로그 기록 실패:', error);
        }
    };

    const handleDownload = async (doc: Document) => {
        try {
            // 첫 번째 파일 다운로드 (문서는 보통 파일 1개)
            if (doc.files && doc.files.length > 0) {
                const file = doc.files[0];
                // 실제 파일 URL이 있으면 다운로드
                if (file.url && file.url !== '#') {
                    // 다운로드 이력 먼저 기록 (실패해도 아래 다운로드는 진행)
                    await logDownload(file);
                    // FileUtils의 downloadFile 함수 사용
                    await FileUtils.downloadFile(file.url, file.name);
                } else {
                    alert('파일 URL이 유효하지 않습니다.');
                }
            } else {
                alert('다운로드할 파일이 없습니다.');
            }
        } catch (error) {
            console.error('Failed to download file:', error);
            alert('파일 다운로드에 실패했습니다.');
        }
    };

    const handleEdit = (doc: Document) => {
        setEditingDocument(doc);
        setEditTitle(doc.title);
        setShowEditModal(true);
    };

    const handleUpdate = async () => {
        if (!editingDocument || !editTitle) {
            alert('제목을 입력해주세요.');
            return;
        }

        try {
            await staffClient.patch(`/article/${editingDocument.id}`, {
                title: editTitle
            });

            // 성공 시 모달 닫기
            setShowEditModal(false);
            setEditingDocument(null);
            setEditTitle('');

            // 문서 목록 새로고침
            await fetchDocuments();

            alert('문서가 수정되었습니다.');
        } catch (error) {
            console.error('Failed to update document:', error);
            alert('문서 수정에 실패했습니다.');
        }
    };

    const handleDelete = async (doc: Document) => {
        if (!window.confirm(`"${doc.title}" 문서를 삭제하시겠습니까?\n이 작업은 취소할 수 없습니다.`)) {
            return;
        }

        try {
            await staffClient.delete(`/article/${doc.id}`);

            // 문서 목록 및 카운트 새로고침
            await fetchDocuments();
            await fetchCategoryCounts();

            alert('문서가 삭제되었습니다.');
        } catch (error) {
            console.error('Failed to delete document:', error);
            alert('문서 삭제에 실패했습니다.');
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (!bytes) return '-';
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ko-KR').replace(/\. /g, '.').replace(/\.$/, '');
    };

    const handleBoardSelect = (board: Board) => {
        setSelectedBoard(board);
        setCategoryCounts({}); // 카운트 초기화
        // 첫 번째 카테고리 자동 선택
        if (board.categories && board.categories.length > 0) {
            setSelectedCategory(board.categories[0].id);
        }
    };

    const getBoardLabel = (board: Board) =>
        (board.document_category && categoryNames[board.document_category]) || board.name;

    if (loading) {
        return <Loading text="자료실을 불러오는 중..." />;
    }

    if (boards.length === 0) {
        return (
            <Section>
                <p className="py-8 text-center text-sm text-muted-foreground">
                    등록된 자료실이 없습니다.
                </p>
            </Section>
        );
    }

    const selectedCategoryName =
        selectedBoard?.categories?.find((c) => c.id === selectedCategory)?.name || '문서';

    const columns: Column<Document>[] = [
        {
            key: 'title',
            header: '파일명',
            render: (value) => (
                <div className="flex min-w-0 items-center gap-2">
                    <FileText className="h-4 w-4 shrink-0 text-neutral-400" strokeWidth={1.5} />
                    <span className="truncate text-foreground">{value}</span>
                </div>
            ),
        },
        {
            key: 'files',
            header: '크기',
            width: '100px',
            align: 'right',
            render: (_value, row) => (
                <span className="text-muted-foreground tabular-nums">
                    {row.files?.[0] ? formatFileSize(row.files[0].size) : '-'}
                </span>
            ),
        },
        {
            key: 'writer',
            header: '작성자',
            width: '120px',
            align: 'center',
            render: (_value, row) =>
                row.writer?.name ? (
                    row.writer.name
                ) : (
                    <span className="text-neutral-400">알 수 없음</span>
                ),
        },
        {
            key: 'created_at',
            header: '등록일',
            width: '110px',
            align: 'center',
            render: (value) => (
                <span className="text-muted-foreground tabular-nums">{formatDate(value)}</span>
            ),
        },
    ];

    return (
        <div>
            {/* 관리 구분 (일반/설계/공정/시공/준공) */}
            <Tabs
                tabs={boards.map((board) => ({ id: String(board.id), label: getBoardLabel(board) }))}
                activeTab={selectedBoard ? String(selectedBoard.id) : undefined}
                onTabChange={(tabId) => {
                    const board = boards.find((b) => String(b.id) === tabId);
                    if (board) handleBoardSelect(board);
                }}
                useQueryParam={false}
                variant="pills"
            >
                {selectedBoard && (
                    <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[260px_minmax(0,1fr)]">
                        {/* 카테고리 — 옆 문서 표와 같은 테두리 컨테이너로 높이·톤 맞춤 */}
                        <TableContainer title="카테고리">
                            <ul>
                                {selectedBoard.categories?.map((category) => {
                                    const active = selectedCategory === category.id;
                                    return (
                                        <li key={category.id} className="border-b border-border last:border-b-0">
                                            <button
                                                type="button"
                                                onClick={() => setSelectedCategory(category.id)}
                                                className={cn(
                                                    'flex w-full items-start gap-2 px-4 py-2.5 text-left text-sm transition-colors duration-fast',
                                                    active
                                                        ? 'bg-navy-50 text-primary font-medium'
                                                        : 'text-neutral-700 hover:bg-muted hover:text-foreground'
                                                )}
                                            >
                                                <span
                                                    className={cn(
                                                        'w-5 shrink-0 tabular-nums',
                                                        active ? 'text-primary' : 'text-neutral-400'
                                                    )}
                                                >
                                                    {category.order}.
                                                </span>
                                                <span className="min-w-0 flex-1 break-keep">{category.name}</span>
                                                <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                                                    {categoryCounts[category.id] !== undefined
                                                        ? categoryCounts[category.id]
                                                        : '…'}
                                                </span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </TableContainer>

                        {/* 문서 목록 */}
                        <TableContainer
                            title={selectedCategoryName}
                            resultCount={documents.length}
                            actions={
                                <>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        onChange={handleFileSelect}
                                        disabled={uploading}
                                        className="hidden"
                                    />
                                    <Button
                                        size="small"
                                        icon={<Upload />}
                                        disabled={uploading}
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        파일 업로드
                                    </Button>
                                </>
                            }
                        >
                            <Table
                                columns={columns}
                                data={documents}
                                pagination
                                pageSize={15}
                                emptyMessage="등록된 문서가 없습니다."
                                actionsColumnWidth="120px"
                                actions={(doc) => (
                                    <>
                                        <Button
                                            size="small"
                                            variant="outline"
                                            className="w-8 px-0"
                                            onClick={() => handleDownload(doc)}
                                            disabled={!doc.files?.[0]}
                                            title="다운로드"
                                            aria-label="다운로드"
                                            icon={<Download />}
                                        />
                                        <Button
                                            size="small"
                                            variant="outline"
                                            className="w-8 px-0"
                                            onClick={() => handleEdit(doc)}
                                            title="수정"
                                            aria-label="수정"
                                            icon={<Pencil />}
                                        />
                                        <Button
                                            size="small"
                                            variant="danger-outline"
                                            className="w-8 px-0"
                                            onClick={() => handleDelete(doc)}
                                            title="삭제"
                                            aria-label="삭제"
                                            icon={<Trash2 />}
                                        />
                                    </>
                                )}
                            />
                        </TableContainer>
                    </div>
                )}
            </Tabs>

            {/* 파일 업로드 모달 */}
            <Modal
                isOpen={showUploadModal}
                onClose={() => setShowUploadModal(false)}
                title="파일 업로드"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setShowUploadModal(false)}>
                            취소
                        </Button>
                        <Button onClick={handleFileUpload} disabled={uploading || !uploadTitle}>
                            {uploading ? '업로드 중...' : '업로드'}
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Input
                        label="제목"
                        required
                        value={uploadTitle}
                        onChange={(e) => setUploadTitle(e.target.value)}
                        placeholder="문서 제목을 입력하세요"
                        autoFocus
                    />
                    <InfoField label="선택된 파일">
                        <FileText className="h-4 w-4 shrink-0 text-neutral-400" strokeWidth={1.5} />
                        <span className="min-w-0 flex-1 truncate">{selectedFile?.name}</span>
                        <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                            {formatFileSize(selectedFile?.size || 0)}
                        </span>
                    </InfoField>
                    <InfoField label="게시판">
                        {selectedBoard && categoryNames[selectedBoard.document_category || ''] || selectedBoard?.name}
                    </InfoField>
                    <InfoField label="카테고리">
                        {selectedBoard?.categories?.find(c => c.id === selectedCategory)?.name}
                    </InfoField>
                </div>
            </Modal>

            {/* 수정 모달 */}
            <Modal
                isOpen={showEditModal && !!editingDocument}
                onClose={() => setShowEditModal(false)}
                title="문서 수정"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setShowEditModal(false)}>
                            취소
                        </Button>
                        <Button onClick={handleUpdate} disabled={!editTitle}>
                            수정
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Input
                        label="제목"
                        required
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        placeholder="문서 제목을 입력하세요"
                        autoFocus
                    />
                    <InfoField label="파일">
                        <FileText className="h-4 w-4 shrink-0 text-neutral-400" strokeWidth={1.5} />
                        <span className="min-w-0 flex-1 truncate">
                            {editingDocument?.files?.[0]?.name || '파일 없음'}
                        </span>
                        {editingDocument?.files?.[0] && (
                            <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                                {formatFileSize(editingDocument.files[0].size)}
                            </span>
                        )}
                    </InfoField>
                </div>
            </Modal>
        </div>
    );
};

// 모달 안 읽기 전용 정보 행
const InfoField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-neutral-700">{label}</span>
        <div className="flex min-h-9 items-center gap-2 rounded border border-border bg-neutral-50 px-3 py-2 text-sm text-foreground">
            {children}
        </div>
    </div>
);

export default DocumentLibrary;
